// src/pages/brand-collaborations-client-stories/components/PartnershipEvolution.jsx
import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const PartnershipEvolution = () => {
  const [selectedEvolution, setSelectedEvolution] = useState(0);

  const evolutionStories = [
    {
      id: 1,
      client: "TechVision Corporation",
      industry: "Technology",
      initialProject: {
        title: "Product Launch Documentation",
        year: 2020,
        scope: "Single event coverage",
        budget: "$25,000",
        image: "https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=800"
      },
      currentPartnership: {
        title: "Strategic Creative Partner",
        year: 2024,
        scope: "Complete brand visual ecosystem",
        budget: "$500,000+ annually",
        image: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?q=80&w=800&auto=format&fit=crop"
      },
      milestones: [
        { year: 2020, event: "Initial product launch project", impact: "150% engagement boost" },
        { year: 2021, event: "Quarterly content series contract", impact: "Brand consistency across platforms" },
        { year: 2022, event: "Global campaign leadership", impact: "300% international reach expansion" },
        { year: 2023, event: "Innovation showcase partnership", impact: "Industry recognition awards" },
        { year: 2024, event: "Strategic creative partnership", impact: "Complete visual ecosystem management" }
      ],
      evolution: {
        projectCount: "45+ projects completed",
        relationship: "5-year strategic partnership",
        growth: "2,000% value increase",
        satisfaction: "99.5% approval rating"
      },
      testimonial: "What started as a single project has evolved into our most trusted creative partnership. They\'ve become an extension of our team, understanding our vision before we even articulate it.",
      executive: "Sarah Chen, CMO"
    },
    {
      id: 2,
      client: "Prestige Properties Group",
      industry: "Luxury Real Estate",
      initialProject: {
        title: "Single Property Showcase",
        year: 2019,
        scope: "Individual luxury property",
        budget: "$15,000",
        image: "https://images.pexels.com/photos/1643383/pexels-photo-1643383.jpeg?auto=compress&cs=tinysrgb&w=800"
      },
      currentPartnership: {
        title: "Exclusive Visual Content Partner",
        year: 2024,
        scope: "Complete portfolio management",
        budget: "$750,000+ annually",
        image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=800&auto=format&fit=crop"
      },
      milestones: [
        { year: 2019, event: "First luxury property project", impact: "Sold 40% above market value" },
        { year: 2020, event: "Multi-property portfolio contract", impact: "250% sales acceleration" },
        { year: 2021, event: "Virtual tour innovation leadership", impact: "500% virtual engagement" },
        { year: 2022, event: "International market expansion", impact: "Global luxury positioning" },
        { year: 2024, event: "Exclusive partnership agreement", impact: "Market leadership achievement" }
      ],
      evolution: {
        projectCount: "75+ properties showcased",
        relationship: "6-year exclusive partnership",
        growth: "5,000% value increase",
        satisfaction: "98.9% client satisfaction"
      },
      testimonial: "From our first property to managing our entire luxury portfolio, this partnership has redefined how we present premium real estate. They\'ve elevated our brand to unprecedented levels.",
      executive: "Michael Rodriguez, Development Director"
    },
    {
      id: 3,
      client: "Global Business Forums",
      industry: "Corporate Events",
      initialProject: {
        title: "Annual Conference Coverage",
        year: 2018,
        scope: "Single day event documentation",
        budget: "$12,000",
        image: "https://images.pexels.com/photos/1709003/pexels-photo-1709003.jpeg?auto=compress&cs=tinysrgb&w=800"
      },
      currentPartnership: {
        title: "Official Event Documentation Partner",
        year: 2024,
        scope: "Year-round event ecosystem",
        budget: "$400,000+ annually",
        image: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=800&auto=format&fit=crop"
      },
      milestones: [
        { year: 2018, event: "First conference documentation", impact: "Professional recognition" },
        { year: 2019, event: "Multi-event annual contract", impact: "Consistent quality delivery" },
        { year: 2020, event: "Virtual event innovation", impact: "Pandemic adaptation success" },
        { year: 2022, event: "Global event network expansion", impact: "International event leadership" },
        { year: 2024, event: "Official partnership designation", impact: "Industry standard setting" }
      ],
      evolution: {
        projectCount: "120+ events documented",
        relationship: "7-year strategic partnership",
        growth: "3,300% value increase",
        satisfaction: "99.8% event satisfaction"
      },
      testimonial: "Seven years ago, we hired them for one event. Today, they\'re integral to our success. Their evolution with our needs has been remarkable, setting new industry standards.",
      executive: "James Thompson, Event Director"
    }
  ];

  const currentStory = evolutionStories[selectedEvolution];

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-text-primary mb-6">
            Partnership <span className="text-gradient-gold">Evolution</span>
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Witness how initial projects developed into ongoing strategic relationships, 
            demonstrating our value as creative partners rather than just service providers
          </p>
        </div>

        {/* Evolution Stories Navigation */}
        <div className="flex items-center justify-center mb-12">
          <div className="flex space-x-4">
            {evolutionStories.map((story, index) => (
              <button
                key={story.id}
                onClick={() => setSelectedEvolution(index)}
                className={`px-6 py-3 rounded-lg font-medium text-sm transition-all duration-300 ${
                  selectedEvolution === index
                    ? 'bg-accent text-white shadow-glow'
                    : 'bg-white text-text-secondary hover:text-accent hover:bg-accent/10'
                }`}
              >
                {story.client}
              </button>
            ))}
          </div>
        </div>

        {/* Active Evolution Story */}
        <div className="animate-fade-in">
          {/* Story Header */}
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-text-primary mb-2">{currentStory.client}</h3>
            <p className="text-lg text-accent">{currentStory.industry} Partnership Evolution</p>
          </div>

          {/* Before & After Comparison */}
          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            {/* Initial Project */}
            <div className="bg-white rounded-lg overflow-hidden shadow-subtle">
              <div className="relative h-64">
                <Image
                  src={currentStory.initialProject.image}
                  alt={currentStory.initialProject.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute top-4 left-4">
                  <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-medium">
                    {currentStory.initialProject.year}
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h4 className="text-white font-bold text-xl mb-1">Initial Project</h4>
                  <p className="text-white/80">{currentStory.initialProject.title}</p>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-text-secondary text-sm">Scope</span>
                    <p className="font-medium text-text-primary">{currentStory.initialProject.scope}</p>
                  </div>
                  <div>
                    <span className="text-text-secondary text-sm">Investment</span>
                    <p className="font-medium text-text-primary">{currentStory.initialProject.budget}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Current Partnership */}
            <div className="bg-white rounded-lg overflow-hidden shadow-subtle border-2 border-accent">
              <div className="relative h-64">
                <Image
                  src={currentStory.currentPartnership.image}
                  alt={currentStory.currentPartnership.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute top-4 left-4">
                  <span className="bg-accent text-white px-3 py-1 rounded-full text-sm font-medium">
                    {currentStory.currentPartnership.year}
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h4 className="text-white font-bold text-xl mb-1">Current Partnership</h4>
                  <p className="text-white/80">{currentStory.currentPartnership.title}</p>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-text-secondary text-sm">Scope</span>
                    <p className="font-medium text-text-primary">{currentStory.currentPartnership.scope}</p>
                  </div>
                  <div>
                    <span className="text-text-secondary text-sm">Investment</span>
                    <p className="font-medium text-text-primary">{currentStory.currentPartnership.budget}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Evolution Timeline */}
          <div className="mb-16">
            <h4 className="font-bold text-text-primary text-2xl mb-8 text-center">Partnership Journey</h4>
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-accent transform -translate-x-1/2"></div>
              
              {/* Timeline Items */}
              <div className="space-y-8">
                {currentStory.milestones.map((milestone, index) => (
                  <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                    <div className={`w-5/12 ${index % 2 === 0 ? 'text-right pr-8' : 'text-left pl-8'}`}>
                      <div className="bg-white rounded-lg p-6 shadow-subtle">
                        <div className="text-accent font-bold text-lg mb-2">{milestone.year}</div>
                        <h5 className="font-semibold text-text-primary mb-2">{milestone.event}</h5>
                        <p className="text-text-secondary text-sm">{milestone.impact}</p>
                      </div>
                    </div>
                    <div className="relative">
                      <div className="w-4 h-4 bg-accent rounded-full border-4 border-white shadow-subtle"></div>
                    </div>
                    <div className="w-5/12"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Evolution Metrics */}
          <div className="bg-background-dark rounded-lg p-8 mb-12">
            <h4 className="font-bold text-text-primary-inverse text-2xl mb-8 text-center">
              Partnership Growth Metrics
            </h4>
            <div className="grid md:grid-cols-4 gap-6">
              {Object.entries(currentStory.evolution).map(([key, value], index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-accent/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon name="TrendingUp" size={24} color="#D4AF37" strokeWidth={2} />
                  </div>
                  <div className="text-accent font-bold text-xl mb-1">{value}</div>
                  <div className="text-text-secondary-inverse text-sm capitalize">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Executive Testimonial */}
          <div className="bg-white rounded-lg p-8 shadow-subtle">
            <div className="flex items-start space-x-4">
              <Icon name="Quote" size={32} className="text-accent flex-shrink-0 mt-2" strokeWidth={2} />
              <div>
                <p className="text-text-primary text-xl italic mb-4 leading-relaxed">
                  "{currentStory.testimonial}"
                </p>
                <p className="text-text-secondary font-medium">
                  — {currentStory.executive}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnershipEvolution;